import { useCallback, useState } from "react";
import { updateMaterial } from "@/lib/manufacturingClient";
import type { MfgMsdsStatus } from "@/lib/manufacturing/msdsStatus";

export type UpdateMaterialPayload = {
  material_id: string;
  name?: string;
  code?: string;
  unit?: string;
  category?: string;
  shipping_unit?: string;
  shipping_unit_weight?: number;
  pieces_per_shipping_unit?: number;
  is_active?: boolean;
  /**
   * Dev-sync note 618b2eb5: free-text primary vendor name. Persisted into
   * `metadata.vendor` server-side. Empty string clears; omit to leave
   * untouched.
   */
  vendor?: string;
  /** VEN-2b: existing alias pick or clear (null). */
  primary_alias_id?: string | null;
  /** MSDS-1: SDS requirement triage. */
  msds_status?: MfgMsdsStatus;
  /** Qty that triggers Fill from below-reorder. Null clears; omit to leave untouched. */
  reorder_threshold?: number | null;
  /** True when a $0 unit cost is intentional. */
  allow_zero_cost?: boolean;
};

/**
 * Submit-side helper for the material modal's edit mode. Mirrors the shape
 * of `useManufacturingCreateMaterial` so the modal can swap save targets
 * cleanly: same `{ ok, error? }` return contract, same toast field, same
 * `onCreated` → `onUpdated` callback semantics.
 */
export function useManufacturingUpdateMaterial(opts: { onUpdated: () => void }) {
  const { onUpdated } = opts;
  const [toast, setToast] = useState<string | null>(null);

  const submit = useCallback(
    async (payload: UpdateMaterialPayload) => {
      const r = await updateMaterial(payload);
      if (!r.ok) return { ok: false as const, error: r.error };
      const code = r.data.material?.code ?? "";
      const name = r.data.material?.name ?? "";
      setToast(code ? `Material ${code} (${name}) updated` : `Material "${name}" updated`);
      onUpdated();
      return { ok: true as const };
    },
    [onUpdated]
  );

  const dismissToast = useCallback(() => setToast(null), []);

  return { submit, toast, dismissToast };
}
